import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, ImageOff } from "lucide-react";
import { RiseIn, SplitWords } from "@/components/site/Reveal";
import { api } from "@/lib/api";

// ---------- Index (list) ----------
export function ConditionsIndex() {
  const [items, setItems] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.listConditions().then(setItems).catch((e) => setError(e.message));
  }, []);

  return (
    <div className="pt-36 pb-8" data-testid="conditions-page">
      <section className="px-6 lg:px-12">
        <div className="mx-auto max-w-[1600px] grid grid-cols-12 gap-8">
          <div className="col-span-12 lg:col-span-8">
            <div className="text-[10px] uppercase tracking-[0.3em] text-brass-500 mb-6">
              Chapter 02 · Conditions & Operative Register
            </div>
            <h1 className="font-serif text-5xl md:text-7xl lg:text-8xl leading-[0.92] tracking-[-0.02em] text-navy-600">
              A field guide,
              <br />
              <span className="italic pl-6 md:pl-16">quietly documented.</span>
            </h1>
          </div>
          <RiseIn className="col-span-12 lg:col-span-4 lg:pt-6" delay={0.2}>
            <p className="text-sm md:text-base text-navy-600/75 leading-relaxed max-w-sm">
              Plain-language notes on the twenty-two conditions I see most often
              — what each is, how it presents, and where laser and other
              minimally-invasive techniques have changed the treatment. Case
              images sit with the notes they belong to.
            </p>
          </RiseIn>
        </div>
      </section>

      <section className="px-6 lg:px-12 mt-20" data-testid="conditions-index">
        <div className="mx-auto max-w-[1600px]">
          {error && (
            <div className="text-sm text-destructive" data-testid="conditions-error">
              {error}
            </div>
          )}
          {!items ? (
            <div className="text-[10px] uppercase tracking-[0.24em] text-navy-600/50">
              Loading…
            </div>
          ) : (
            <ul className="divide-y divide-navy-600/20 border-t border-navy-600/20">
              {items.map((c, i) => (
                <RiseIn key={c.slug} delay={Math.min(i, 8) * 0.03}>
                  <li>
                    <Link
                      to={`/conditions/${c.slug}`}
                      data-testid={`condition-link-${c.slug}`}
                      className="group w-full py-8 md:py-10 grid grid-cols-12 gap-6 items-baseline text-left hover:pl-6 transition-[padding] duration-500 ease-out"
                    >
                      <div className="col-span-2 md:col-span-1 chapter-num text-navy-600/40 text-2xl md:text-3xl">
                        {String(i + 1).padStart(2, "0")}
                      </div>
                      <div className="col-span-10 md:col-span-4">
                        <div className="font-serif text-3xl md:text-5xl tracking-tight text-navy-600 group-hover:italic transition-[font-style] duration-500">
                          {c.name}
                        </div>
                        <div className="text-[10px] uppercase tracking-[0.28em] text-brass-500 mt-2">
                          {c.images?.length || 0} case image{(c.images?.length || 0) === 1 ? "" : "s"}
                        </div>
                      </div>
                      <div className="col-span-12 md:col-span-6 text-sm md:text-base text-navy-600/75 leading-relaxed">
                        {c.description}
                      </div>
                      <div className="col-span-12 md:col-span-1 text-[10px] uppercase tracking-[0.24em] text-navy-600/60 justify-self-end">
                        Read →
                      </div>
                    </Link>
                  </li>
                </RiseIn>
              ))}
            </ul>
          )}
        </div>
      </section>
    </div>
  );
}

// ---------- Detail page ----------
export function ConditionDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [c, setC] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    setC(null);
    api.getCondition(slug).then(setC).catch((e) => setError(e.message));
  }, [slug]);

  if (error) {
    return (
      <div className="pt-40 pb-16 px-6 lg:px-12 min-h-[60vh]" data-testid="condition-detail-error">
        <p className="text-sm text-destructive">{error}</p>
        <Link to="/conditions" className="mt-6 inline-flex link-underline text-[11px] uppercase tracking-[0.24em]">
          ← Back to all conditions
        </Link>
      </div>
    );
  }

  if (!c) {
    return (
      <div className="pt-40 pb-16 px-6 lg:px-12" data-testid="condition-detail-loading">
        <div className="text-[10px] uppercase tracking-[0.24em] text-navy-600/50">Loading…</div>
      </div>
    );
  }

  return (
    <div className="pt-36 pb-8" data-testid={`condition-detail-${c.slug}`}>
      <section className="px-6 lg:px-12">
        <div className="mx-auto max-w-3xl">
          <button
            onClick={() => navigate("/conditions")}
            data-testid="detail-back"
            className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] text-navy-600/70 hover:text-navy-600"
          >
            <ArrowLeft size={14} /> All conditions
          </button>

          <RiseIn delay={0.05}>
            <div className="mt-12 text-[10px] uppercase tracking-[0.3em] text-brass-500">
              Condition
            </div>
            <h1
              className="mt-4 leading-[0.95] tracking-[-0.02em] text-navy-600 text-[clamp(2.75rem,7vw,6.5rem)]"
              style={{ fontFamily: "'Cormorant Garamond', 'Playfair Display', serif", fontWeight: 500 }}
            >
              {c.name}
            </h1>
          </RiseIn>

          <RiseIn delay={0.1}>
            <p className="dropcap mt-12 text-lg leading-relaxed text-navy-600/85">{c.description}</p>
          </RiseIn>

          <RiseIn delay={0.15}>
            <section className="mt-16">
              <div className="chapter-num text-brass-500 text-xl">01</div>
              <h2 className="font-serif text-3xl md:text-4xl tracking-tight text-navy-600 mt-2">
                Symptoms
              </h2>
              <p className="mt-6 text-navy-600/85 leading-relaxed">{c.symptoms}</p>
            </section>
          </RiseIn>

          <RiseIn delay={0.2}>
            <section className="mt-16">
              <div className="chapter-num text-brass-500 text-xl">02</div>
              <h2 className="font-serif text-3xl md:text-4xl tracking-tight text-navy-600 mt-2">
                Recent advances in management
              </h2>
              <p className="mt-6 text-navy-600/85 leading-relaxed">{c.advances}</p>
            </section>
          </RiseIn>
        </div>
      </section>

      {/* Case images */}
      <section className="px-6 lg:px-12 mt-24" data-testid="condition-gallery">
        <div className="mx-auto max-w-[1600px]">
          <div className="flex items-baseline justify-between border-t border-navy-600/20 pt-8">
            <div>
              <div className="chapter-num text-brass-500 text-xl">03</div>
              <h2 className="font-serif text-3xl md:text-4xl tracking-tight text-navy-600 mt-2">
                Operative & case images
              </h2>
            </div>
            <div className="text-[10px] uppercase tracking-[0.24em] text-navy-600/50">
              {c.images.length} entr{c.images.length === 1 ? "y" : "ies"}
            </div>
          </div>

          {c.images.length === 0 ? (
            <div
              className="mt-10 border border-dashed border-navy-600/25 py-24 text-center"
              data-testid="condition-gallery-empty"
            >
              <ImageOff size={22} className="mx-auto text-navy-600/40" />
              <p className="mt-4 text-[10px] uppercase tracking-[0.28em] text-navy-600/50">
                Case images will appear here as they are added
              </p>
            </div>
          ) : (
            <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {c.images.map((img, i) => (
                <RiseIn key={img.id} delay={(i % 3) * 0.05}>
                  <figure className="group relative overflow-hidden aspect-[4/5]">
                    <motion.img
                      src={img.data_url}
                      alt={img.caption || c.name}
                      className="w-full h-full object-cover"
                      whileHover={{ scale: 1.05 }}
                      transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
                    />
                    <div className="absolute top-4 left-4 text-[10px] uppercase tracking-[0.28em] text-beige-100 bg-navy-600/60 backdrop-blur px-2.5 py-1">
                      {String(i + 1).padStart(2, "0")}
                    </div>
                    {img.caption ? (
                      <figcaption className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-navy-600/85 via-navy-600/40 to-transparent text-beige-100 text-xs">
                        {img.caption}
                      </figcaption>
                    ) : null}
                  </figure>
                </RiseIn>
              ))}
            </div>
          )}

          <p className="mt-16 max-w-2xl text-xs uppercase tracking-[0.24em] text-navy-600/50 leading-relaxed">
            All images published here are shared with informed patient consent
            and are shown for educational reference. Content is not a substitute
            for a clinical consultation.
          </p>

          <div className="mt-16 border-t border-navy-600/20 pt-10">
            <Link
              to="/book"
              data-testid="detail-book-cta"
              className="group inline-flex items-center gap-3 bg-navy-600 text-beige-100 px-7 py-4 text-[11px] uppercase tracking-[0.28em] hover:bg-navy-700 transition-colors"
            >
              Consult on this
              <ArrowUpRight size={16} className="transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
